import { useNavigate, useLocation } from 'react-router-dom';
import {
  Paper,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Button,
  Typography,
  CircularProgress,
  useMediaQuery,
  Box,
} from '@mui/material';
import { useAuth } from '../Auth/Auth';

const LockIcon = () => (
  <svg
    version='1.1'
    xmlnsXlink='http://www.w3.org/1999/xlink'
    width='16px'
    height='20px'
    xmlns='http://www.w3.org/2000/svg'
  >
    <g transform='matrix(1 0 0 1 -25 -92 )'>
      <path
        d='M 4.444444444444445 6.363636363636365  L 4.444444444444445 9.090909090909092  L 11.555555555555555 9.090909090909092  L 11.555555555555555 6.363636363636365  C 11.555555555555555 5.359848484848484  11.208333333333334 4.502840909090908  10.51388888888889 3.7926136363636354  C 9.819444444444445 3.0823863636363646  8.981481481481483 2.727272727272727  8 2.727272727272727  C 7.018518518518519 2.727272727272727  6.180555555555555 3.0823863636363646  5.486111111111111 3.7926136363636354  C 4.791666666666667 4.502840909090908  4.444444444444445 5.359848484848484  4.444444444444445 6.363636363636365  Z M 15.61111111111111 9.488636363636365  C 15.870370370370372 9.753787878787879  16 10.075757575757574  16 10.454545454545453  L 16 18.636363636363637  C 16 19.015151515151516  15.870370370370372 19.33712121212121  15.61111111111111 19.602272727272727  C 15.351851851851855 19.867424242424242  15.037037037037038 20  14.666666666666666 20  L 1.3333333333333333 20  C 0.9629629629629631 20  0.6481481481481483 19.867424242424242  0.3888888888888889 19.602272727272727  C 0.12962962962962962 19.33712121212121  0 19.015151515151516  0 18.636363636363637  L 0 10.454545454545453  C 0 10.075757575757574  0.12962962962962962 9.753787878787879  0.3888888888888889 9.488636363636365  C 0.6481481481481483 9.223484848484848  0.9629629629629631 9.090909090909092  1.3333333333333333 9.090909090909092  L 1.7777777777777777 9.090909090909092  L 1.7777777777777777 6.363636363636365  C 1.7777777777777777 4.621212121212119  2.388888888888889 3.125  3.611111111111111 1.875  C 4.833333333333333 0.625  6.296296296296297 0  8 0  C 9.703703703703704 0  11.166666666666666 0.625  12.38888888888889 1.875  C 13.61111111111111 3.125  14.222222222222221 4.621212121212119  14.222222222222221 6.363636363636365  L 14.222222222222221 9.090909090909092  L 14.666666666666666 9.090909090909092  C 15.037037037037038 9.090909090909092  15.351851851851855 9.223484848484848  15.61111111111111 9.488636363636365  Z '
        fillRule='nonzero'
        fill='#FFFFFF'
        stroke='none'
        transform='matrix(1 0 0 1 25 92 )'
      />
    </g>
  </svg>
);

export const LoginPage: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:1024px)');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const username = formData.get('email') as string;
    const password = formData.get('password') as string;

    auth.signIn({ username, password }, () => {
      // Send them back to the page they tried to visit when they were
      // redirected to the login page. Use { replace: true } so we don't create
      // another entry in the history stack for the login page.  This means that
      // when they get to the protected page and click the back button, they
      // won't end up back on the login page, which is also really nice for the
      // user experience.
      navigate(from, { replace: true });
    });
  };

  return isMobile ? (
    <Typography variant='h6' padding='30px'>
      Ésta aplicación no es compatible con resoluciones menores a 1024 pixeles.
      Por favor ingrese desde otro dispositivo.
    </Typography>
  ) : (
    <Stack alignItems='center' justifyContent='center' marginTop='100px'>
      <Stack flexDirection='row'>
        <Box
          borderRadius='8px 0 0 8px'
          boxShadow='0 5px 5px rgba(0,0,0,.35)'
          padding='10px'
          sx={{ backgroundColor: '#67686B' }}
        >
          <LockIcon />
        </Box>
        <Paper
          variant='outlined'
          sx={{
            width: `3${isMobile ? '0' : '5'}0px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            borderRadius: ' 0 8px 8px 0',
            backgroundColor: '#E6E7EE',
            boxShadow: '0 5px 5px rgba(0,0,0,.35)',
            border: 'none',
          }}
        >
          <Stack gap='20px' justifyContent='center' alignItems='center'>
            <Typography variant='h2' fontSize='1.5em' fontWeight='bold'>
              {!auth?.token || !auth?.user
                ? 'Acceso identificado'
                : 'Ya se encuentra logueado.'}
            </Typography>
            {(!auth?.token || !auth?.user) && (
              <form
                onSubmit={handleSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '20px',
                  width: '260px',
                }}
              >
                <FormControl>
                  <FormLabel sx={{ color: 'black' }}>Email</FormLabel>
                  <Input
                    disableUnderline
                    name='email'
                    sx={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #C2C2C2',
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel sx={{ color: 'black' }}>Contraseña</FormLabel>
                  <Input
                    disableUnderline
                    name='password'
                    type='password'
                    sx={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #C2C2C2',
                    }}
                  />
                </FormControl>
                <Button
                  variant='contained'
                  type='submit'
                  sx={{ textTransform: 'none' }}
                  disabled={auth?.isLoading}
                >
                  {auth?.isLoading ? <CircularProgress size={25} /> : 'Acceder'}
                </Button>
                {auth?.error && (
                  <Typography color='red'>{auth.error}</Typography>
                )}
              </form>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};
